import { Text } from '@radix-ui/themes';
import { LoaderCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  trpcConsumerGetVerifiedCheckoutCustomerCandidates,
  trpcConsumerGetVerifiedCheckoutCustomerSites,
  trpcConsumerSelectVerifiedCheckoutCustomer,
  trpcConsumerSelectVerifiedCheckoutCustomerSite,
} from '@trashlab/api/src/generated/hooks';

type CheckoutCustomerAccountSelectionProps = {
  cartId: string;
  tenantId: string;
  savedContactRevision: number;
  verificationRevision: number;
  currentSiteAddress?: AddressSummary | null;
  onBeforeSelect?: () => Promise<void>;
  onSelectionReadyChange?: (ready: boolean) => void;
};

type AddressSummary = {
  streetAddressPartOne?: string | null;
  streetAddressPartTwo?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
};

type Candidate = {
  customerId: string;
  customerName: string;
  maskedEmail: string | null;
  maskedPhoneNumber: string | null;
  totalOrderCount: number;
  jobSites: Array<{
    customerSiteId: string;
    name: string;
    orderCount: number;
  }>;
  hiddenJobSiteCount: number;
  recommended: boolean;
  selected: boolean;
};

type JobSite = {
  customerSiteId: string;
  name: string;
  address: AddressSummary;
  totalOrderCount: number;
  latestOrderCreatedAtTs: Date | string | null;
  selected: boolean;
};

type PendingCustomerSelection =
  | { type: 'REUSE_CUSTOMER'; customerId: string }
  | { type: 'CREATE_NEW_CUSTOMER' }
  | null;

function orderCountLabel(count: number) {
  return `${count} prior order${count === 1 ? '' : 's'}`;
}

function jobSiteOrderLabel(count: number) {
  return `${count} order${count === 1 ? '' : 's'}`;
}

function formatAddress(address?: AddressSummary | null) {
  if (!address) {
    return null;
  }

  return [
    address.streetAddressPartOne,
    address.streetAddressPartTwo,
    [address.city, address.state].filter(Boolean).join(', '),
    address.postalCode,
  ]
    .filter(Boolean)
    .join(' ');
}

function latestOrderLabel(value: Date | string | null) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return `Latest order ${date.toLocaleDateString()}`;
}

function CandidateOption({
  candidate,
  selected,
  disabled,
  loading,
  onSelect,
}: {
  candidate: Candidate;
  selected: boolean;
  disabled: boolean;
  loading: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Select ${candidate.customerName}`}
      data-tl={`verified-customer-option-${candidate.customerId}`}
      disabled={disabled}
      aria-busy={loading}
      onClick={onSelect}
      className={[
        'relative w-full overflow-hidden rounded-md border p-3 text-left transition-colors',
        selected
          ? 'border-[var(--accent-8)] bg-[var(--accent-2)]'
          : 'border-gray-200 bg-white',
        disabled && !loading
          ? 'cursor-not-allowed opacity-60'
          : disabled
            ? 'cursor-not-allowed'
            : 'cursor-pointer',
      ].join(' ')}
    >
      <div className={loading ? 'opacity-35' : undefined}>
        <div className="flex items-start justify-between gap-2">
          <Text size="2" weight="bold" className="min-w-0 truncate">
            {candidate.customerName}
          </Text>
          {candidate.recommended && (
            <span className="shrink-0 rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
              Recommended
            </span>
          )}
        </div>
        <Text size="1" color="gray" className="mt-1 block">
          {orderCountLabel(candidate.totalOrderCount)}
        </Text>
        {(candidate.maskedPhoneNumber || candidate.maskedEmail) && (
          <Text size="1" color="gray" className="mt-1 block">
            {[candidate.maskedPhoneNumber, candidate.maskedEmail]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        )}
        <Text size="1" weight="medium" className="mt-2 block">
          Yes, this is my account.
        </Text>
      </div>
      {loading && (
        <span
          role="status"
          aria-label="Selecting account"
          className="absolute inset-0 flex items-center justify-center bg-white/70"
        >
          <LoaderCircle className="size-5 animate-spin text-[var(--accent-9)]" />
        </span>
      )}
    </button>
  );
}

function JobSiteOption({
  site,
  selected,
  disabled,
  onSelect,
}: {
  site: JobSite;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const latest = latestOrderLabel(site.latestOrderCreatedAtTs);
  const address = formatAddress(site.address);

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Select ${site.name}`}
      data-tl={`verified-customer-site-option-${site.customerSiteId}`}
      disabled={disabled}
      onClick={onSelect}
      className={[
        'w-full rounded-md border p-3 text-left transition-colors',
        selected
          ? 'border-[var(--accent-8)] bg-[var(--accent-2)]'
          : 'border-gray-200 bg-white',
        disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
      ].join(' ')}
    >
      <Text size="2" weight="bold" className="block min-w-0 truncate">
        {site.name}
      </Text>
      {address && (
        <Text size="1" color="gray" className="mt-1 block">
          {address}
        </Text>
      )}
      <Text size="1" color="gray" className="mt-1 block">
        {jobSiteOrderLabel(site.totalOrderCount)}
        {latest ? ` · ${latest}` : ''}
      </Text>
    </button>
  );
}

function NewSiteOption({
  address,
  selected,
  disabled,
  onSelect,
}: {
  address?: AddressSummary | null;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const formattedAddress = formatAddress(address);

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label="Use a new delivery location"
      data-tl="verified-customer-site-option-new"
      disabled={disabled}
      onClick={onSelect}
      className={[
        'w-full rounded-md border p-3 text-left transition-colors',
        selected
          ? 'border-[var(--accent-8)] bg-[var(--accent-2)]'
          : 'border-gray-200 bg-white',
        disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
      ].join(' ')}
    >
      <Text size="2" weight="bold" className="block">
        Use a new delivery location
      </Text>
      {formattedAddress && (
        <Text size="1" color="gray" className="mt-1 block">
          {formattedAddress}
        </Text>
      )}
    </button>
  );
}

export function CheckoutCustomerAccountSelection({
  cartId,
  tenantId,
  savedContactRevision,
  verificationRevision,
  currentSiteAddress,
  onBeforeSelect,
  onSelectionReadyChange,
}: CheckoutCustomerAccountSelectionProps) {
  const candidatesQuery =
    trpcConsumerGetVerifiedCheckoutCustomerCandidates.useQuery(
      { cartId, tenantId },
      {
        refetchOnWindowFocus: false,
      },
    );
  const sitesQuery = trpcConsumerGetVerifiedCheckoutCustomerSites.useQuery(
    { cartId, tenantId },
    {
      refetchOnWindowFocus: false,
    },
  );
  const candidatesContext =
    trpcConsumerGetVerifiedCheckoutCustomerCandidates.useContext();
  const sitesContext = trpcConsumerGetVerifiedCheckoutCustomerSites.useContext();
  const selectCustomer =
    trpcConsumerSelectVerifiedCheckoutCustomer.useMutation({
      onSuccess: () => ({ skipInvalidation: true }),
    });
  const selectCustomerSite =
    trpcConsumerSelectVerifiedCheckoutCustomerSite.useMutation();
  const [error, setError] = useState<string | null>(null);
  const [pendingSelection, setPendingSelection] =
    useState<PendingCustomerSelection>(null);
  const lastRefreshKeyRef = useRef(
    `${savedContactRevision}:${verificationRevision}`,
  );

  useEffect(() => {
    const refreshKey = `${savedContactRevision}:${verificationRevision}`;
    if (lastRefreshKeyRef.current === refreshKey) {
      return;
    }

    lastRefreshKeyRef.current = refreshKey;
    void candidatesQuery.refetch();
    void sitesQuery.refetch();
  }, [candidatesQuery, savedContactRevision, sitesQuery, verificationRevision]);

  const candidates = candidatesQuery.data?.candidates ?? [];
  const identityDecision =
    candidatesQuery.data?.identityDecision ?? 'UNDECIDED';
  const selectedCustomerId = useMemo(
    () =>
      identityDecision === 'REUSE_CUSTOMER'
        ? (candidatesQuery.data?.selectedCustomerId ??
          candidates.find((candidate) => candidate.selected)?.customerId ??
          null)
        : null,
    [candidates, candidatesQuery.data?.selectedCustomerId, identityDecision],
  );
  const requiresSelection = candidates.length > 0;
  const sites = sitesQuery.data?.sites ?? [];
  const customerResolved = sitesQuery.data?.customerResolved ?? false;
  const selectedCustomerSiteId =
    sitesQuery.data?.selectedCustomerSiteId ??
    sites.find((site) => site.selected)?.customerSiteId ??
    null;
  const showSiteSelection = customerResolved && sites.length > 0;
  const selectionReady =
    !candidatesQuery.isLoading &&
    !sitesQuery.isLoading &&
    !selectCustomerSite.isLoading &&
    pendingSelection === null &&
    (!requiresSelection || identityDecision !== 'UNDECIDED');

  useEffect(() => {
    onSelectionReadyChange?.(selectionReady);
  }, [onSelectionReadyChange, selectionReady]);

  if (!requiresSelection && !showSiteSelection) {
    return null;
  }

  const handleSelect = async (customerId: string | null) => {
    if (pendingSelection !== null) {
      return;
    }

    setError(null);
    const selection: Exclude<PendingCustomerSelection, null> =
      customerId === null
        ? { type: 'CREATE_NEW_CUSTOMER' }
        : { type: 'REUSE_CUSTOMER', customerId };
    setPendingSelection(selection);

    try {
      await onBeforeSelect?.();
      const result = await selectCustomer.mutateAsync({
        cartId,
        tenantId,
        decision: selection,
      });

      candidatesContext.setData({ cartId, tenantId }, {
        candidates: candidates.map((candidate) => ({
          ...candidate,
          selected:
            result.identityDecision === 'REUSE_CUSTOMER' &&
            candidate.customerId === result.selectedCustomerId,
        })),
        selectionRequired: false,
        selectedCustomerId: result.selectedCustomerId,
        identityDecision: result.identityDecision,
      });

      if (result.identityDecision === 'REUSE_CUSTOMER') {
        await sitesQuery.refetch();
      } else {
        sitesContext.setData({ cartId, tenantId }, {
          customerResolved: false,
          selectedCustomerId: null,
          selectedCustomerSiteId: null,
          sites: [],
        });
      }
    } catch {
      setError('Could not select account.');
    } finally {
      setPendingSelection(null);
    }
  };

  const handleSelectSite = async (customerSiteId: string | null) => {
    setError(null);

    try {
      await onBeforeSelect?.();
      const refreshed = await sitesQuery.refetch();

      if (!refreshed.data?.customerResolved) {
        setError('Please verify your contact again.');
        return;
      }

      const siteStillValid =
        customerSiteId === null ||
        refreshed.data.sites.some(
          (site) => site.customerSiteId === customerSiteId,
        );

      if (!siteStillValid) {
        setError('Please choose a valid job site.');
        return;
      }

      await selectCustomerSite.mutateAsync({
        cartId,
        tenantId,
        customerSiteId,
      });
      await sitesQuery.refetch();
    } catch {
      setError('Could not select job site.');
    }
  };

  return (
    <div
      className="rounded-md border border-gray-200 bg-gray-50 p-3"
      data-tl="verified-customer-account-selection"
    >
      {requiresSelection && (
        <>
          <div className="mb-2">
            <Text size="2" weight="bold" className="block">
              Is this your account?
            </Text>
            <Text size="1" color="gray" className="block">
              We found {candidates.length === 1 ? 'an account' : 'accounts'} for
              your verified contact. Confirm your identity before continuing.
            </Text>
          </div>
          <div className="flex flex-col gap-2">
            {candidates.map((candidate) => (
              <CandidateOption
                key={candidate.customerId}
                candidate={candidate}
                selected={
                  selectedCustomerId === candidate.customerId ||
                  (pendingSelection?.type === 'REUSE_CUSTOMER' &&
                    pendingSelection.customerId === candidate.customerId)
                }
                disabled={pendingSelection !== null}
                loading={
                  pendingSelection?.type === 'REUSE_CUSTOMER' &&
                  pendingSelection.customerId === candidate.customerId
                }
                onSelect={() => void handleSelect(candidate.customerId)}
              />
            ))}
            <button
              type="button"
              aria-pressed={
                identityDecision === 'CREATE_NEW_CUSTOMER' ||
                pendingSelection?.type === 'CREATE_NEW_CUSTOMER'
              }
              aria-busy={pendingSelection?.type === 'CREATE_NEW_CUSTOMER'}
              data-tl="verified-customer-option-new"
              disabled={pendingSelection !== null}
              onClick={() => void handleSelect(null)}
              className={[
                'relative w-full overflow-hidden rounded-md border p-3 text-left transition-colors',
                identityDecision === 'CREATE_NEW_CUSTOMER' ||
                pendingSelection?.type === 'CREATE_NEW_CUSTOMER'
                  ? 'border-[var(--accent-8)] bg-[var(--accent-2)]'
                  : 'border-gray-200 bg-white',
                pendingSelection &&
                pendingSelection.type !== 'CREATE_NEW_CUSTOMER'
                  ? 'cursor-not-allowed opacity-60'
                  : pendingSelection
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer',
              ].join(' ')}
            >
              <div
                className={
                  pendingSelection?.type === 'CREATE_NEW_CUSTOMER'
                    ? 'opacity-35'
                    : undefined
                }
              >
                <Text size="2" weight="bold" className="block">
                  This isn’t me
                </Text>
                <Text size="1" color="gray" className="mt-1 block">
                  Create a new account using the information entered above.
                </Text>
              </div>
              {pendingSelection?.type === 'CREATE_NEW_CUSTOMER' && (
                <span
                  role="status"
                  aria-label="Creating new account"
                  className="absolute inset-0 flex items-center justify-center bg-white/70"
                >
                  <LoaderCircle className="size-5 animate-spin text-[var(--accent-9)]" />
                </span>
              )}
            </button>
          </div>
        </>
      )}

      {showSiteSelection && (
        <div
          className={requiresSelection ? 'mt-4' : undefined}
          data-tl="verified-customer-site-selection"
        >
          <div className="mb-2">
            <Text size="2" weight="bold" className="block">
              Choose delivery location
            </Text>
            <Text size="1" color="gray" className="block">
              Use an existing job site or keep the new delivery location.
            </Text>
          </div>
          <div className="flex flex-col gap-2">
            <NewSiteOption
              address={currentSiteAddress}
              selected={selectedCustomerSiteId === null}
              disabled={selectCustomerSite.isLoading}
              onSelect={() => void handleSelectSite(null)}
            />
            {sites.map((site) => (
              <JobSiteOption
                key={site.customerSiteId}
                site={site}
                selected={selectedCustomerSiteId === site.customerSiteId}
                disabled={selectCustomerSite.isLoading}
                onSelect={() => void handleSelectSite(site.customerSiteId)}
              />
            ))}
          </div>
        </div>
      )}
      {error && (
        <Text size="1" color="red" className="mt-2 block">
          {error}
        </Text>
      )}
    </div>
  );
}
